<template>
  <div class="flex flex-col items-center">
    <div @click="showFilters = !showFilters" class="w-full mb-6 flex justify-end items-center cursor-pointer">
      <h3 class="text-right">Advance Filters</h3>
      <i v-if="!showFilters" class="el-icon-arrow-down el-icon--right"></i>
      <i v-if="showFilters" class="el-icon-arrow-up el-icon--right"></i>
    </div>
    <!--#region filters  -->
    <div v-if="showFilters" class="filter-container w-full mb-6">
      <div class="flex flex-row flex-wrap items-center">
        <div title="Order ID" class="mb-2 pr-2 w-full sm:w-1/4 flex-none">
          <el-input clearable placeholder="ID" v-model="filter.id"> </el-input>
        </div>
        <div title="Reference ID" class="mb-2 pr-2 w-full sm:w-1/4 flex-none">
          <el-input clearable placeholder="Reference ID" v-model="filter.order_ref"> </el-input>
        </div>
        <div title="Shipping Tracking Number" class="mb-2 pr-2 w-full sm:w-1/4 flex-none">
          <el-input clearable placeholder="Shipping Tracking Number" v-model="filter.shippingTrackingNumber">
          </el-input>
        </div>
        <div title="Order Status" class="mb-2 pr-2">
          <el-select clearable v-model="filter.status" placeholder="Select Status">
            <el-option
              v-for="item in statusOptions"
              :key="item.name"
              :label="item.displayName + ' (' + item.countOrders + ')'"
              :value="item.name"
            >
            </el-option>
          </el-select>
        </div>
        <div class="mb-2 pr-2 relative">
          <el-date-picker
            v-model="filter.created_at"
            type="datetimerange"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
          >
          </el-date-picker>
          <el-tooltip
            class="item absolute info-button"
            effect="dark"
            content="Filter orders by created date"
            placement="bottom-end"
          >
            <span class="el-icon-info text-gray-400"></span>
          </el-tooltip>
        </div>
      </div>
      <div class="flex justify-end">
        <el-button type="info" @click="resetForm()">Reset Search</el-button>
        <el-button type="primary" @click="search()">Search</el-button>
      </div>
    </div>
    <!--#endregion -->
    <div class="w-full">
      <div class="flex justify-between">
        <div>
          <span class="font-bold">Total Orders</span>:
          {{ totalOrders }}
        </div>
        <div class="w-full md:w-1/4">
          <form @submit="filterBySearchKey">
            <el-input placeholder="Type something" prefix-icon="el-icon-search" v-model="filter.search"> </el-input>
          </form>
        </div>
      </div>
      <!--#region order table  -->
      <orders-table
        @paginate="getOrders"
        :paginationInfo="paginationInfo"
        :isLoading="isLoading"
        :orders="orders"
        :templates="tableCols"
      ></orders-table>
      <!--#endregion -->
    </div>
    <!--#endregion -->
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { orderService } from '../../services/index';
import OrdersTable from '../../components/orders-table.vue';
import { areObjectsEqual, removePropertiesWithEmptyString, formatDate } from '../../utils';
import { OrderClientModel, OrderCountClientModel } from '../../models/client';
import { PaginatedApiModel } from '../../models/api/response';
import { ElNotification } from 'element-plus';
import { PromiseWithCancel } from '../../models/common';

export default defineComponent({
  components: {
    OrdersTable,
  },
  data() {
    return {
      totalOrders: 0,
      updatingOrders: {},
      isLoading: false,
      hasPagination: false,
      orders: [] as OrderClientModel[],
      filter: this.getDefaultFilters(),
      previousFilter: this.getDefaultFilters(),
      showFilters: false,
      statusOptions: [] as OrderCountClientModel[],
      paginationInfo: null as PaginatedApiModel<OrderClientModel> | null,
      perPage: 10,
      tableCols: [{ label: 'Order Line Items', props: 'totalLineItems' }],
      getOrdersPromise: null as PromiseWithCancel<PaginatedApiModel<OrderClientModel>> | null,
    };
  },
  async created() {
    if (this.$route.query.status) {
      this.filter.status = this.$route.query.status;
      this.showFilters = true;
    }
    this.previousFilter = { ...this.filter };
    await this.getOrders();
    this.getOrderStatus();
  },
  methods: {
    onStateChange(filterStatus) {
      this.evaluateCheckboxValues(filterStatus);
    },
    evaluateCheckboxValues(filterStatus) {
      if (filterStatus.status.state) {
        this.filter[filterStatus.indentifier] = filterStatus.status.value;
        return;
      }
      delete this.filter[filterStatus.indentifier];
    },
    async resetForm() {
      const checkBoxRefNames = ['sampleOrderCheckBoxRef', 'xqcCheckBoxRef', 'reprintCheckBoxRef', 'reorderCheckBoxRef'];
      checkBoxRefNames.forEach((checkBoxRef) => (this.$refs[checkBoxRef] as HTMLFormElement).reset());
      this.filter = this.getDefaultFilters();
      this.previousFilter = this.getDefaultFilters();
      await this.getOrders();
    },
    filterBySearchKey: function (event) {
      event.preventDefault();
      this.search();
    },
    async search() {
      if (areObjectsEqual(this.previousFilter, this.filter)) {
        return;
      }
      this.previousFilter = { ...this.filter };
      await this.getOrders(1);
    },
    getDefaultFilters() {
      return {
        id: '',
        order_ref: '',
        customerName: '',
        created_at: '',
        shippingTrackingNumber: '',
        status: '',
      };
    },
    formatDatePipe: function (order) {
      return formatDate(String(order.dateCreated));
    },
    async getOrders(page = 1) {
      this.totalOrders = 0;
      try {
        this.isLoading = true;
        const filter = { ...this.previousFilter };
        // datepicker is not providing date in correct format so we need to change the date format here
        if (filter.created_at) {
          filter.created_at = filter.created_at.map((date) => formatDate(new Date(date).toString())).join(',');
        }
        if (this.getOrdersPromise) {
          this.getOrdersPromise.cancel();
        }
        this.getOrdersPromise = orderService.getOrders({
          page,
          total: this.perPage,
          ...removePropertiesWithEmptyString(filter),
        });
        const response = await this.getOrdersPromise;
        this.getOrdersPromise = null;
        this.orders = response.data;
        this.totalOrders = response.total;
        this.paginationInfo = response;
        this.hasPagination = Boolean(this.paginationInfo && this.orders.length);
      } catch (e) {
        // 401 error will be handled by response interceptor
        if (e.response.status !== 401) {
          ElNotification({
            title: 'Error',
            type: 'error',
            message: 'Failed to load requested data. Please try again later.',
            position: 'bottom-right',
          });
        }
      }
      this.isLoading = false;
    },
    async getOrderStatus() {
      const orderStats = await orderService.getOrderCountList();
      this.statusOptions = orderStats.stats;
    },
  },
});
</script>
<style lang="scss" scoped>
.tableContainer ::v-deep(tr) {
  cursor: pointer;
}

.info-button {
  top: calc(50% - 8px);
  right: 20px;
}

::v-deep(.cell) {
  text-transform: capitalize;
}

.action-icons:hover {
  margin-top: -1px;
}
</style>
